/**
 * Convert any axios / fetch error payload into a safe, human-readable string.
 *
 * Why this exists:
 *   FastAPI + Pydantic v2 returns validation errors as an ARRAY of objects
 *   with shape `{type, loc, msg, input, url}`. If you pass that directly to
 *   `toast.error()` or render it in JSX, React throws error #31 ("Objects
 *   are not valid as a React child") and blanks the screen.
 *
 *   Use this util in every catch-block that surfaces an API error.
 *
 * Examples handled:
 *   - string                                                → returned as-is
 *   - { detail: "Some message" }                            → "Some message"
 *   - { detail: [ {loc:["body","field"], msg:"missing"} ] } → "body.field: missing"
 *   - axios error object                                    → unwraps response.data
 *   - native Error                                          → .message
 *   - anything else                                         → "Unexpected error"
 */
export function formatErrorMessage(input, fallback = 'Operation failed') {
  if (!input) return fallback;

  // Axios error: unwrap response.data
  const data =
    (input && input.response && input.response.data) !== undefined
      ? input.response.data
      : input;

  // Plain string
  if (typeof data === 'string') return data;

  // FastAPI: { detail: ... }
  const detail = data && typeof data === 'object' ? data.detail : undefined;

  // Pydantic validation array: [{loc, msg, type, input, url}, ...]
  if (Array.isArray(detail)) {
    if (detail.length === 0) return fallback;
    const lines = detail
      .map((d) => {
        if (!d) return null;
        if (typeof d === 'string') return d;
        if (typeof d === 'object') {
          const loc = Array.isArray(d.loc)
            ? d.loc
                .filter((p) => p !== 'body' && p !== 'query' && p !== 'path')
                .join('.')
            : '';
          const msg = d.msg || d.message || d.type || 'invalid value';
          return loc ? `${loc}: ${msg}` : String(msg);
        }
        return String(d);
      })
      .filter(Boolean);
    return lines.length ? lines.join('\n') : fallback;
  }

  // Plain detail string
  if (typeof detail === 'string') return detail;

  // Generic { message }
  if (data && typeof data === 'object' && typeof data.message === 'string') {
    return data.message;
  }

  // Native Error
  if (input && typeof input === 'object' && typeof input.message === 'string') {
    return input.message;
  }

  // Last-ditch: never render the raw object
  try {
    return JSON.stringify(data).slice(0, 300);
  } catch (_e) {
    return fallback;
  }
}

export default formatErrorMessage;
